import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Switch } from '../../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { 
  Layout, Image, Users, Briefcase, Star, Link2, Settings, Upload, Plus, Edit, Trash2, 
  Save, Eye, GripVertical, Video, Youtube, ChevronRight, ChevronDown, Palette, Monitor,
  Film, Megaphone, Shield, Check, X, RefreshCw, ExternalLink, MousePointer, Menu
} from 'lucide-react';
import api from '../../lib/axios';
import { toast } from 'sonner';

// Icon mapping for service families
const ICON_MAP = {
  'Palette': Palette,
  'Monitor': Monitor,
  'Film': Film,
  'Megaphone': Megaphone,
  'Shield': Shield,
};

const AdminCMSPage = () => {
  const [activeTab, setActiveTab] = useState('hero');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // CMS Data States
  const [hero, setHero] = useState(null);
  const [serviceFamilies, setServiceFamilies] = useState([]);
  const [solutions, setSolutions] = useState([]);
  const [team, setTeam] = useState([]);
  const [portfolio, setPortfolio] = useState([]);
  const [partners, setPartners] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [socialLinks, setSocialLinks] = useState(null);
  const [siteSettings, setSiteSettings] = useState(null);
  
  // Modal states
  const [editModal, setEditModal] = useState({ open: false, type: null, data: null });
  const [deleteModal, setDeleteModal] = useState({ open: false, type: null, id: null });
  
  // Expanded sections
  const [expandedFamily, setExpandedFamily] = useState(null);

  useEffect(() => {
    loadAllCMSData();
  }, []);

  const loadAllCMSData = async () => {
    setLoading(true);
    try {
      const response = await api.get('/cms/all');
      setHero(response.data.hero);
      setServiceFamilies(response.data.service_families || []);
      setSolutions(response.data.solutions || []);
      setTeam(response.data.team || []);
      setPortfolio(response.data.portfolio || []);
      setPartners(response.data.partners || []);
      setTestimonials(response.data.testimonials || []);
      setSocialLinks(response.data.social_links);
      setSiteSettings(response.data.site_settings || {
        header_button: { text: 'Espace Client', url: '/login', is_visible: true },
        floating_menu: { show_admin_button: true, show_scroll_top: true, show_brochure: true, show_callback: true, show_quote: true },
        footer: { copyright_text: "MSC L'Agence. Tous droits réservés.", version: "1.0.0" }
      });
    } catch (error) {
      console.error('Error loading CMS data:', error);
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveHero = async () => {
    setSaving(true);
    try {
      await api.put('/cms/hero', hero);
      toast.success('Section Hero mise à jour');
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveSocialLinks = async () => {
    setSaving(true);
    try {
      await api.put('/cms/social-links', socialLinks);
      toast.success('Liens réseaux sociaux mis à jour');
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveSiteSettings = async () => {
    setSaving(true);
    try {
      await api.put('/cms/site-settings', siteSettings);
      toast.success('Paramètres du site mis à jour');
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handleFileUpload = async (file, folder = 'cms') => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('folder', folder);
    
    try {
      const response = await api.post('/cms/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      return response.data.url;
    } catch (error) {
      toast.error('Erreur lors de l\'upload');
      return null;
    }
  };

  const handleCreateItem = async (type, data) => {
    try {
      const endpoints = {
        'service-family': '/cms/service-families',
        'solution': '/cms/solutions',
        'team': '/cms/team',
        'portfolio': '/cms/portfolio',
        'partner': '/cms/partners',
        'testimonial': '/cms/testimonials'
      };
      
      await api.post(endpoints[type], data);
      toast.success('Élément créé avec succès');
      loadAllCMSData();
      setEditModal({ open: false, type: null, data: null });
    } catch (error) {
      toast.error('Erreur lors de la création');
    }
  };

  const handleUpdateItem = async (type, id, data) => {
    try {
      const endpoints = {
        'service-family': `/cms/service-families/${id}`,
        'solution': `/cms/solutions/${id}`,
        'team': `/cms/team/${id}`,
        'portfolio': `/cms/portfolio/${id}`,
        'partner': `/cms/partners/${id}`,
        'testimonial': `/cms/testimonials/${id}`
      };
      
      await api.put(endpoints[type], data);
      toast.success('Élément mis à jour');
      loadAllCMSData();
      setEditModal({ open: false, type: null, data: null });
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const handleDeleteItem = async () => {
    const { type, id } = deleteModal;
    try {
      const endpoints = {
        'service-family': `/cms/service-families/${id}`,
        'solution': `/cms/solutions/${id}`,
        'team': `/cms/team/${id}`,
        'portfolio': `/cms/portfolio/${id}`,
        'partner': `/cms/partners/${id}`,
        'testimonial': `/cms/testimonials/${id}`
      };
      
      await api.delete(endpoints[type]);
      toast.success('Élément supprimé');
      loadAllCMSData();
      setDeleteModal({ open: false, type: null, id: null });
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const runMigration = async () => {
    try {
      const response = await api.post('/cms/migrate');
      toast.success('Migration terminée');
      loadAllCMSData();
    } catch (error) {
      toast.error('Erreur lors de la migration');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">CMS - Gestion du contenu</h1>
          <p className="text-slate-500">Gérez tout le contenu de votre site vitrine</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={runMigration}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Initialiser les données
          </Button>
          <Button variant="outline" onClick={() => window.open('/site', '_blank')}>
            <Eye className="h-4 w-4 mr-2" />
            Voir le site
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid grid-cols-4 lg:grid-cols-8 gap-1">
          <TabsTrigger value="hero" className="flex items-center gap-1">
            <Layout className="h-4 w-4" />
            <span className="hidden sm:inline">Hero</span>
          </TabsTrigger>
          <TabsTrigger value="services" className="flex items-center gap-1">
            <Briefcase className="h-4 w-4" />
            <span className="hidden sm:inline">Services</span>
          </TabsTrigger>
          <TabsTrigger value="team" className="flex items-center gap-1">
            <Users className="h-4 w-4" />
            <span className="hidden sm:inline">Équipe</span>
          </TabsTrigger>
          <TabsTrigger value="portfolio" className="flex items-center gap-1">
            <Image className="h-4 w-4" />
            <span className="hidden sm:inline">Réalisations</span>
          </TabsTrigger>
          <TabsTrigger value="partners" className="flex items-center gap-1">
            <Star className="h-4 w-4" />
            <span className="hidden sm:inline">Partenaires</span>
          </TabsTrigger>
          <TabsTrigger value="testimonials" className="flex items-center gap-1">
            <Star className="h-4 w-4" />
            <span className="hidden sm:inline">Témoignages</span>
          </TabsTrigger>
          <TabsTrigger value="social" className="flex items-center gap-1">
            <Link2 className="h-4 w-4" />
            <span className="hidden sm:inline">Réseaux</span>
          </TabsTrigger>
          <TabsTrigger value="settings" className="flex items-center gap-1">
            <Settings className="h-4 w-4" />
            <span className="hidden sm:inline">Paramètres</span>
          </TabsTrigger>
        </TabsList>

        {/* Hero Section Tab */}
        <TabsContent value="hero" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Section Hero</CardTitle>
              <CardDescription>Personnalisez le titre, sous-titre et appels à l'action de la page d'accueil</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {hero && (
                <>
                  <div className="grid gap-4 md:grid-cols-2">
                    <div className="space-y-2">
                      <Label>Titre principal</Label>
                      <Input 
                        value={hero.title || ''} 
                        onChange={(e) => setHero({...hero, title: e.target.value})}
                        placeholder="Créons ensemble votre succès digital"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>URL de la vidéo de fond</Label>
                      <Input 
                        value={hero.video_url || ''} 
                        onChange={(e) => setHero({...hero, video_url: e.target.value})}
                        placeholder="https://..."
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Sous-titre</Label>
                    <Textarea 
                      value={hero.subtitle || ''} 
                      onChange={(e) => setHero({...hero, subtitle: e.target.value})}
                      placeholder="Description courte..."
                      rows={2}
                    />
                  </div>
                  
                  <div className="grid gap-4 md:grid-cols-2">
                    <div className="space-y-2">
                      <Label>Texte bouton principal</Label>
                      <Input 
                        value={hero.cta_text || ''} 
                        onChange={(e) => setHero({...hero, cta_text: e.target.value})}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Lien bouton principal</Label>
                      <Input 
                        value={hero.cta_link || ''} 
                        onChange={(e) => setHero({...hero, cta_link: e.target.value})}
                      />
                    </div>
                  </div>
                  
                  <div className="grid gap-4 md:grid-cols-2">
                    <div className="space-y-2">
                      <Label>Texte bouton secondaire</Label>
                      <Input 
                        value={hero.secondary_cta_text || ''} 
                        onChange={(e) => setHero({...hero, secondary_cta_text: e.target.value})}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Lien bouton secondaire</Label>
                      <Input 
                        value={hero.secondary_cta_link || ''} 
                        onChange={(e) => setHero({...hero, secondary_cta_link: e.target.value})}
                      />
                    </div>
                  </div>

                  {/* Stats */}
                  <div className="space-y-2">
                    <Label>Statistiques (affichées dans le Hero)</Label>
                    <div className="grid gap-2 md:grid-cols-4">
                      {(hero.stats || []).map((stat, idx) => (
                        <div key={idx} className="flex gap-2">
                          <Input 
                            value={stat.value} 
                            onChange={(e) => {
                              const newStats = [...hero.stats];
                              newStats[idx].value = e.target.value;
                              setHero({...hero, stats: newStats});
                            }}
                            placeholder="150+"
                            className="w-20"
                          />
                          <Input 
                            value={stat.label} 
                            onChange={(e) => {
                              const newStats = [...hero.stats];
                              newStats[idx].label = e.target.value;
                              setHero({...hero, stats: newStats});
                            }}
                            placeholder="Projets"
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  <Button onClick={handleSaveHero} disabled={saving}>
                    <Save className="h-4 w-4 mr-2" />
                    {saving ? 'Enregistrement...' : 'Enregistrer'}
                  </Button>
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Services Tab */}
        <TabsContent value="services" className="space-y-4">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-lg font-semibold">Familles de services & Solutions</h2>
              <p className="text-sm text-slate-500">Gérez vos services et leurs tarifs</p>
            </div>
            <Button onClick={() => setEditModal({ open: true, type: 'service-family', data: { is_active: true, order: serviceFamilies.length + 1 } })}>
              <Plus className="h-4 w-4 mr-2" />
              Nouvelle famille
            </Button>
          </div>

          <div className="space-y-4">
            {serviceFamilies.map((family) => {
              const IconComponent = ICON_MAP[family.icon] || Briefcase;
              const familySolutions = solutions.filter(s => s.family_id === family.id);
              const isExpanded = expandedFamily === family.id;
              
              return (
                <Card key={family.id}>
                  <CardHeader className="cursor-pointer" onClick={() => setExpandedFamily(isExpanded ? null : family.id)}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`p-2 rounded-lg bg-gradient-to-br ${family.color} text-white`}>
                          <IconComponent className="h-5 w-5" />
                        </div>
                        <div>
                          <CardTitle className="text-base">{family.title}</CardTitle>
                          <CardDescription>{family.description}</CardDescription>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant="outline">{familySolutions.length} solutions</Badge>
                        <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); setEditModal({ open: true, type: 'service-family', data: family }); }}>
                          <Edit className="h-4 w-4" />
                        </Button>
                        {isExpanded ? <ChevronDown className="h-5 w-5" /> : <ChevronRight className="h-5 w-5" />}
                      </div>
                    </div>
                  </CardHeader>
                  
                  {isExpanded && (
                    <CardContent className="border-t">
                      <div className="pt-4 space-y-3">
                        <div className="flex justify-between items-center">
                          <span className="text-sm font-medium text-slate-600">Solutions de cette famille</span>
                          <Button size="sm" variant="outline" onClick={() => setEditModal({ open: true, type: 'solution', data: { family_id: family.id, is_active: true, order: familySolutions.length + 1 } })}>
                            <Plus className="h-3 w-3 mr-1" />
                            Ajouter
                          </Button>
                        </div>
                        
                        {familySolutions.length === 0 ? (
                          <p className="text-sm text-slate-400 italic">Aucune solution pour cette famille</p>
                        ) : (
                          <div className="grid gap-2">
                            {familySolutions.map((solution) => (
                              <div key={solution.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                                <div>
                                  <span className="font-medium">{solution.name}</span>
                                  {solution.badge && <Badge className="ml-2" variant="secondary">{solution.badge}</Badge>}
                                  <p className="text-sm text-slate-500">{solution.price_range}</p>
                                </div>
                                <div className="flex gap-1">
                                  <Button variant="ghost" size="sm" onClick={() => setEditModal({ open: true, type: 'solution', data: solution })}>
                                    <Edit className="h-4 w-4" />
                                  </Button>
                                  <Button variant="ghost" size="sm" onClick={() => setDeleteModal({ open: true, type: 'solution', id: solution.id })}>
                                    <Trash2 className="h-4 w-4 text-red-500" />
                                  </Button>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </CardContent>
                  )}
                </Card>
              );
            })}
          </div>
        </TabsContent>

        {/* Team Tab */}
        <TabsContent value="team" className="space-y-4">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-lg font-semibold">Équipe</h2>
              <p className="text-sm text-slate-500">Gérez les membres de votre équipe</p>
            </div>
            <Button onClick={() => setEditModal({ open: true, type: 'team', data: { is_active: true, skills: [], order: team.length + 1 } })}>
              <Plus className="h-4 w-4 mr-2" />
              Nouveau membre
            </Button>
          </div>

          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {team.map((member) => (
              <Card key={member.id}>
                <CardContent className="pt-6">
                  <div className="flex items-start gap-4">
                    <div className={`w-16 h-16 rounded-full bg-gradient-to-br ${member.color} flex items-center justify-center text-white text-xl font-bold`}>
                      {member.photo_url ? (
                        <img src={member.photo_url} alt={member.name} className="w-full h-full rounded-full object-cover" />
                      ) : (
                        member.name?.charAt(0)
                      )}
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold">{member.name}</h3>
                      <p className="text-sm text-slate-500">{member.role}</p>
                      <p className="text-xs text-slate-400 mt-1">{member.experience}</p>
                    </div>
                  </div>
                  <div className="mt-4 flex flex-wrap gap-1">
                    {(member.skills || []).map((skill, idx) => (
                      <Badge key={idx} variant="secondary" className="text-xs">{skill}</Badge>
                    ))}
                  </div>
                  <div className="mt-4 flex justify-end gap-1">
                    <Button variant="ghost" size="sm" onClick={() => setEditModal({ open: true, type: 'team', data: member })}>
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => setDeleteModal({ open: true, type: 'team', id: member.id })}>
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        {/* Portfolio Tab */}
        <TabsContent value="portfolio" className="space-y-4">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-lg font-semibold">Réalisations</h2>
              <p className="text-sm text-slate-500">Gérez votre portfolio de projets</p>
            </div>
            <Button onClick={() => setEditModal({ open: true, type: 'portfolio', data: { is_active: true, tags: [], results: [], order: portfolio.length + 1 } })}>
              <Plus className="h-4 w-4 mr-2" />
              Nouveau projet
            </Button>
          </div>

          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {portfolio.map((project) => (
              <Card key={project.id} className="overflow-hidden">
                <div className="h-40 bg-slate-100">
                  {project.image && (
                    <img src={project.image} alt={project.title} className="w-full h-full object-cover" />
                  )}
                </div>
                <CardContent className="pt-4">
                  <div className="flex items-start justify-between">
                    <div>
                      <Badge variant="outline" className="mb-2">{project.category}</Badge>
                      <h3 className="font-semibold">{project.title}</h3>
                      <p className="text-sm text-slate-500">{project.client}</p>
                    </div>
                  </div>
                  <div className="mt-2 flex flex-wrap gap-1">
                    {(project.tags || []).slice(0, 3).map((tag, idx) => (
                      <Badge key={idx} variant="secondary" className="text-xs">{tag}</Badge>
                    ))}
                  </div>
                  <div className="mt-4 flex justify-end gap-1">
                    <Button variant="ghost" size="sm" onClick={() => setEditModal({ open: true, type: 'portfolio', data: project })}>
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => setDeleteModal({ open: true, type: 'portfolio', id: project.id })}>
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        {/* Partners Tab */}
        <TabsContent value="partners" className="space-y-4">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-lg font-semibold">Logos partenaires</h2>
              <p className="text-sm text-slate-500">Les logos qui défilent sur la page d'accueil</p>
            </div>
            <Button onClick={() => setEditModal({ open: true, type: 'partner', data: { is_active: true, order: partners.length + 1 } })}>
              <Plus className="h-4 w-4 mr-2" />
              Nouveau partenaire
            </Button>
          </div>

          <div className="grid gap-4 md:grid-cols-4 lg:grid-cols-6">
            {partners.map((partner) => (
              <Card key={partner.id}>
                <CardContent className="pt-6 text-center">
                  <div className="h-16 flex items-center justify-center mb-2">
                    {partner.logo_url && (
                      <img src={partner.logo_url} alt={partner.name} className="max-h-full max-w-full object-contain" />
                    )}
                  </div>
                  <p className="text-sm font-medium truncate">{partner.name}</p>
                  <div className="mt-2 flex justify-center gap-1">
                    <Button variant="ghost" size="sm" onClick={() => setEditModal({ open: true, type: 'partner', data: partner })}>
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => setDeleteModal({ open: true, type: 'partner', id: partner.id })}>
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        {/* Testimonials Tab */}
        <TabsContent value="testimonials" className="space-y-4">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-lg font-semibold">Témoignages clients</h2>
              <p className="text-sm text-slate-500">Avis écrits ou vidéos YouTube</p>
            </div>
            <Button onClick={() => setEditModal({ open: true, type: 'testimonial', data: { is_active: true, rating: 5, is_video: false, order: testimonials.length + 1 } })}>
              <Plus className="h-4 w-4 mr-2" />
              Nouveau témoignage
            </Button>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            {testimonials.map((testimonial) => (
              <Card key={testimonial.id}>
                <CardContent className="pt-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-full bg-slate-200 flex items-center justify-center">
                      {testimonial.avatar ? (
                        <img src={testimonial.avatar} alt={testimonial.name} className="w-full h-full rounded-full object-cover" />
                      ) : (
                        <Users className="h-6 w-6 text-slate-400" />
                      )}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold">{testimonial.name}</h3>
                        {testimonial.is_video && <Badge variant="secondary"><Youtube className="h-3 w-3 mr-1" />Vidéo</Badge>}
                      </div>
                      <p className="text-sm text-slate-500">{testimonial.role} - {testimonial.company}</p>
                      <div className="flex gap-0.5 mt-1">
                        {[...Array(testimonial.rating || 5)].map((_, i) => (
                          <Star key={i} className="h-3 w-3 fill-yellow-400 text-yellow-400" />
                        ))}
                      </div>
                    </div>
                  </div>
                  <p className="mt-4 text-sm text-slate-600 italic">"{testimonial.quote}"</p>
                  <div className="mt-4 flex justify-between items-center">
                    <Badge variant="outline">{testimonial.service}</Badge>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="sm" onClick={() => setEditModal({ open: true, type: 'testimonial', data: testimonial })}>
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => setDeleteModal({ open: true, type: 'testimonial', id: testimonial.id })}>
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        {/* Social Links Tab */}
        <TabsContent value="social" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Réseaux sociaux</CardTitle>
              <CardDescription>Liens vers vos profils sur les réseaux sociaux</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {socialLinks && (
                <>
                  <div className="grid gap-4 md:grid-cols-2">
                    <div className="space-y-2">
                      <Label>Facebook</Label>
                      <Input 
                        value={socialLinks.facebook || ''} 
                        onChange={(e) => setSocialLinks({...socialLinks, facebook: e.target.value})}
                        placeholder="https://facebook.com/..."
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Instagram</Label>
                      <Input 
                        value={socialLinks.instagram || ''} 
                        onChange={(e) => setSocialLinks({...socialLinks, instagram: e.target.value})}
                        placeholder="https://instagram.com/..."
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>LinkedIn</Label>
                      <Input 
                        value={socialLinks.linkedin || ''} 
                        onChange={(e) => setSocialLinks({...socialLinks, linkedin: e.target.value})}
                        placeholder="https://linkedin.com/..."
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>YouTube</Label>
                      <Input 
                        value={socialLinks.youtube || ''} 
                        onChange={(e) => setSocialLinks({...socialLinks, youtube: e.target.value})}
                        placeholder="https://youtube.com/..."
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Twitter/X</Label>
                      <Input 
                        value={socialLinks.twitter || ''} 
                        onChange={(e) => setSocialLinks({...socialLinks, twitter: e.target.value})}
                        placeholder="https://twitter.com/..."
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>TikTok</Label>
                      <Input 
                        value={socialLinks.tiktok || ''} 
                        onChange={(e) => setSocialLinks({...socialLinks, tiktok: e.target.value})}
                        placeholder="https://tiktok.com/..."
                      />
                    </div>
                  </div>
                  
                  <Button onClick={handleSaveSocialLinks} disabled={saving}>
                    <Save className="h-4 w-4 mr-2" />
                    {saving ? 'Enregistrement...' : 'Enregistrer'}
                  </Button>
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Site Settings Tab */}
        <TabsContent value="settings" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Paramètres du site</CardTitle>
              <CardDescription>Configurez le bouton header, le menu flottant et le footer</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {siteSettings && (
                <>
                  {/* Header Button */}
                  <div className="space-y-4 p-4 border rounded-lg">
                    <h3 className="font-semibold flex items-center gap-2">
                      <MousePointer className="h-4 w-4" />
                      Bouton Header (Espace Client)
                    </h3>
                    <div className="grid gap-4 md:grid-cols-3">
                      <div className="space-y-2">
                        <Label>Texte du bouton</Label>
                        <Input 
                          value={siteSettings.header_button?.text || ''} 
                          onChange={(e) => setSiteSettings({
                            ...siteSettings, 
                            header_button: {...siteSettings.header_button, text: e.target.value}
                          })}
                          placeholder="Espace Client"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>URL de destination</Label>
                        <Input 
                          value={siteSettings.header_button?.url || ''} 
                          onChange={(e) => setSiteSettings({
                            ...siteSettings, 
                            header_button: {...siteSettings.header_button, url: e.target.value}
                          })}
                          placeholder="/login"
                        />
                      </div>
                      <div className="flex items-center gap-2 pt-6">
                        <Switch 
                          checked={siteSettings.header_button?.is_visible !== false} 
                          onCheckedChange={(checked) => setSiteSettings({
                            ...siteSettings, 
                            header_button: {...siteSettings.header_button, is_visible: checked}
                          })}
                        />
                        <Label>Afficher le bouton</Label>
                      </div>
                    </div>
                  </div>

                  {/* Floating Menu */}
                  <div className="space-y-4 p-4 border rounded-lg">
                    <h3 className="font-semibold flex items-center gap-2">
                      <Menu className="h-4 w-4" />
                      Menu flottant (boutons à droite)
                    </h3>
                    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                      <div className="flex items-center gap-2">
                        <Switch 
                          checked={siteSettings.floating_menu?.show_scroll_top !== false} 
                          onCheckedChange={(checked) => setSiteSettings({
                            ...siteSettings, 
                            floating_menu: {...siteSettings.floating_menu, show_scroll_top: checked}
                          })}
                        />
                        <Label>Retour en haut</Label>
                      </div>
                      <div className="flex items-center gap-2">
                        <Switch 
                          checked={siteSettings.floating_menu?.show_brochure !== false} 
                          onCheckedChange={(checked) => setSiteSettings({
                            ...siteSettings, 
                            floating_menu: {...siteSettings.floating_menu, show_brochure: checked}
                          })}
                        />
                        <Label>Télécharger brochure</Label>
                      </div>
                      <div className="flex items-center gap-2">
                        <Switch 
                          checked={siteSettings.floating_menu?.show_callback !== false} 
                          onCheckedChange={(checked) => setSiteSettings({
                            ...siteSettings, 
                            floating_menu: {...siteSettings.floating_menu, show_callback: checked}
                          })}
                        />
                        <Label>Être rappelé</Label>
                      </div>
                      <div className="flex items-center gap-2">
                        <Switch 
                          checked={siteSettings.floating_menu?.show_quote !== false} 
                          onCheckedChange={(checked) => setSiteSettings({
                            ...siteSettings, 
                            floating_menu: {...siteSettings.floating_menu, show_quote: checked}
                          })}
                        />
                        <Label>Devis rapide</Label>
                      </div>
                    </div>
                  </div>

                  {/* Footer Settings */}
                  <div className="space-y-4 p-4 border rounded-lg">
                    <h3 className="font-semibold">Footer</h3>
                    <div className="grid gap-4 md:grid-cols-2">
                      <div className="space-y-2">
                        <Label>Texte copyright</Label>
                        <Input 
                          value={siteSettings.footer?.copyright_text || ''} 
                          onChange={(e) => setSiteSettings({
                            ...siteSettings, 
                            footer: {...siteSettings.footer, copyright_text: e.target.value}
                          })}
                          placeholder="MSC L'Agence. Tous droits réservés."
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Version</Label>
                        <Input 
                          value={siteSettings.footer?.version || ''} 
                          onChange={(e) => setSiteSettings({
                            ...siteSettings, 
                            footer: {...siteSettings.footer, version: e.target.value}
                          })}
                          placeholder="1.0.0"
                        />
                      </div>
                    </div>
                    <div className="flex items-center gap-2 pt-2">
                      <Switch 
                        checked={siteSettings.footer?.show_admin_icon !== false} 
                        onCheckedChange={(checked) => setSiteSettings({
                          ...siteSettings, 
                          footer: {...siteSettings.footer, show_admin_icon: checked}
                        })}
                      />
                      <Label>Afficher l'icône Admin (à côté de CGV)</Label>
                    </div>
                  </div>
                  
                  <Button onClick={handleSaveSiteSettings} disabled={saving}>
                    <Save className="h-4 w-4 mr-2" />
                    {saving ? 'Enregistrement...' : 'Enregistrer'}
                  </Button>
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Edit Modal */}
      <EditItemModal 
        modal={editModal}
        onClose={() => setEditModal({ open: false, type: null, data: null })}
        onSave={(data) => {
          if (editModal.data?.id) {
            handleUpdateItem(editModal.type, editModal.data.id, data);
          } else {
            handleCreateItem(editModal.type, data);
          }
        }}
        serviceFamilies={serviceFamilies}
        handleFileUpload={handleFileUpload}
      />

      {/* Delete Confirmation Modal */}
      <Dialog open={deleteModal.open} onOpenChange={(open) => !open && setDeleteModal({ open: false, type: null, id: null })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirmer la suppression</DialogTitle>
          </DialogHeader>
          <p>Êtes-vous sûr de vouloir supprimer cet élément ? Cette action est irréversible.</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteModal({ open: false, type: null, id: null })}>
              Annuler
            </Button>
            <Button variant="destructive" onClick={handleDeleteItem}>
              Supprimer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Edit Modal Component
const EditItemModal = ({ modal, onClose, onSave, serviceFamilies, handleFileUpload }) => {
  const [formData, setFormData] = useState({});
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (modal.data) {
      setFormData({ ...modal.data });
    }
  }, [modal.data]);

  const handleUpload = async (e, field) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setUploading(true);
    const url = await handleFileUpload(file);
    if (url) {
      setFormData(prev => ({ ...prev, [field]: url }));
    }
    setUploading(false);
  };

  const renderForm = () => {
    switch (modal.type) {
      case 'service-family':
        return (
          <div className="space-y-4">
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label>Titre</Label>
                <Input value={formData.title || ''} onChange={(e) => setFormData({...formData, title: e.target.value})} />
              </div>
              <div className="space-y-2">
                <Label>Titre court</Label>
                <Input value={formData.short_title || ''} onChange={(e) => setFormData({...formData, short_title: e.target.value})} />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea value={formData.description || ''} onChange={(e) => setFormData({...formData, description: e.target.value})} />
            </div>
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label>Icône (Palette, Monitor, Film, Megaphone, Shield)</Label>
                <Input value={formData.icon || ''} onChange={(e) => setFormData({...formData, icon: e.target.value})} />
              </div>
              <div className="space-y-2">
                <Label>Couleur (ex: from-pink-500 to-rose-600)</Label>
                <Input value={formData.color || ''} onChange={(e) => setFormData({...formData, color: e.target.value})} />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Features (une par ligne)</Label>
              <Textarea 
                value={(formData.features || []).join('\n')} 
                onChange={(e) => setFormData({...formData, features: e.target.value.split('\n').filter(f => f.trim())})}
                rows={4}
              />
            </div>
          </div>
        );
      
      case 'solution':
        return (
          <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-2">
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label>Nom</Label>
                <Input value={formData.name || ''} onChange={(e) => setFormData({...formData, name: e.target.value})} />
              </div>
              <div className="space-y-2">
                <Label>Famille de service</Label>
                <Select value={formData.family_id || ''} onValueChange={(v) => setFormData({...formData, family_id: v})}>
                  <SelectTrigger><SelectValue placeholder="Sélectionner..." /></SelectTrigger>
                  <SelectContent>
                    {serviceFamilies.map(f => (
                      <SelectItem key={f.id} value={f.id}>{f.title}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Description courte</Label>
              <Input value={formData.description || ''} onChange={(e) => setFormData({...formData, description: e.target.value})} />
            </div>
            <div className="space-y-2">
              <Label>Description détaillée</Label>
              <Textarea value={formData.long_description || ''} onChange={(e) => setFormData({...formData, long_description: e.target.value})} />
            </div>
            <div className="grid gap-4 md:grid-cols-3">
              <div className="space-y-2">
                <Label>Prix à partir de (€)</Label>
                <Input type="number" value={formData.price_from || ''} onChange={(e) => setFormData({...formData, price_from: parseFloat(e.target.value) || 0})} />
              </div>
              <div className="space-y-2">
                <Label>Fourchette de prix</Label>
                <Input value={formData.price_range || ''} onChange={(e) => setFormData({...formData, price_range: e.target.value})} placeholder="1 500€ - 4 000€ HT" />
              </div>
              <div className="space-y-2">
                <Label>Délai de livraison</Label>
                <Input value={formData.delivery_time || ''} onChange={(e) => setFormData({...formData, delivery_time: e.target.value})} placeholder="2-4 semaines" />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Badge (optionnel: Populaire, Nouveau...)</Label>
              <Input value={formData.badge || ''} onChange={(e) => setFormData({...formData, badge: e.target.value})} />
            </div>
            <div className="space-y-2">
              <Label>Caractéristiques (une par ligne)</Label>
              <Textarea 
                value={(formData.features || []).join('\n')} 
                onChange={(e) => setFormData({...formData, features: e.target.value.split('\n').filter(f => f.trim())})}
                rows={3}
              />
            </div>
            <div className="space-y-2">
              <Label>Ce qui est inclus (une par ligne)</Label>
              <Textarea 
                value={(formData.includes || []).join('\n')} 
                onChange={(e) => setFormData({...formData, includes: e.target.value.split('\n').filter(f => f.trim())})}
                rows={3}
              />
            </div>
            <div className="space-y-2">
              <Label>Pourquoi le faire ?</Label>
              <Textarea value={formData.why_do_it || ''} onChange={(e) => setFormData({...formData, why_do_it: e.target.value})} rows={2} />
            </div>
            <div className="space-y-2">
              <Label>Idéal pour (une par ligne)</Label>
              <Textarea 
                value={(formData.ideal_for || []).join('\n')} 
                onChange={(e) => setFormData({...formData, ideal_for: e.target.value.split('\n').filter(f => f.trim())})}
                rows={2}
              />
            </div>
          </div>
        );

      case 'team':
        return (
          <div className="space-y-4">
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label>Nom</Label>
                <Input value={formData.name || ''} onChange={(e) => setFormData({...formData, name: e.target.value})} />
              </div>
              <div className="space-y-2">
                <Label>Poste</Label>
                <Input value={formData.role || ''} onChange={(e) => setFormData({...formData, role: e.target.value})} />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Bio</Label>
              <Textarea value={formData.bio || ''} onChange={(e) => setFormData({...formData, bio: e.target.value})} />
            </div>
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label>Expérience</Label>
                <Input value={formData.experience || ''} onChange={(e) => setFormData({...formData, experience: e.target.value})} placeholder="10+ ans" />
              </div>
              <div className="space-y-2">
                <Label>Couleur (ex: from-blue-500 to-cyan-500)</Label>
                <Input value={formData.color || ''} onChange={(e) => setFormData({...formData, color: e.target.value})} />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Compétences (une par ligne)</Label>
              <Textarea 
                value={(formData.skills || []).join('\n')} 
                onChange={(e) => setFormData({...formData, skills: e.target.value.split('\n').filter(f => f.trim())})}
                rows={3}
              />
            </div>
            <div className="space-y-2">
              <Label>Photo</Label>
              <div className="flex gap-2">
                <Input value={formData.photo_url || ''} onChange={(e) => setFormData({...formData, photo_url: e.target.value})} placeholder="URL ou upload" />
                <label className="cursor-pointer">
                  <Button variant="outline" as="span" disabled={uploading}>
                    <Upload className="h-4 w-4" />
                  </Button>
                  <input type="file" className="hidden" accept="image/*" onChange={(e) => handleUpload(e, 'photo_url')} />
                </label>
              </div>
            </div>
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label>LinkedIn</Label>
                <Input value={formData.linkedin_url || ''} onChange={(e) => setFormData({...formData, linkedin_url: e.target.value})} />
              </div>
              <div className="space-y-2">
                <Label>Email</Label>
                <Input value={formData.email || ''} onChange={(e) => setFormData({...formData, email: e.target.value})} />
              </div>
            </div>
          </div>
        );

      case 'portfolio':
        return (
          <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-2">
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label>Titre du projet</Label>
                <Input value={formData.title || ''} onChange={(e) => setFormData({...formData, title: e.target.value})} />
              </div>
              <div className="space-y-2">
                <Label>Client</Label>
                <Input value={formData.client || ''} onChange={(e) => setFormData({...formData, client: e.target.value})} />
              </div>
            </div>
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label>Catégorie</Label>
                <Select value={formData.category || ''} onValueChange={(v) => setFormData({...formData, category: v})}>
                  <SelectTrigger><SelectValue placeholder="Sélectionner..." /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="graphique">Graphique</SelectItem>
                    <SelectItem value="web">Web</SelectItem>
                    <SelectItem value="video">Vidéo</SelectItem>
                    <SelectItem value="audio">Audio</SelectItem>
                    <SelectItem value="marketing">Marketing</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Slug (URL)</Label>
                <Input value={formData.slug || ''} onChange={(e) => setFormData({...formData, slug: e.target.value})} />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea value={formData.description || ''} onChange={(e) => setFormData({...formData, description: e.target.value})} />
            </div>
            <div className="space-y-2">
              <Label>Image principale</Label>
              <div className="flex gap-2">
                <Input value={formData.image || ''} onChange={(e) => setFormData({...formData, image: e.target.value})} placeholder="URL ou upload" />
                <label className="cursor-pointer">
                  <Button variant="outline" as="span" disabled={uploading}>
                    <Upload className="h-4 w-4" />
                  </Button>
                  <input type="file" className="hidden" accept="image/*" onChange={(e) => handleUpload(e, 'image')} />
                </label>
              </div>
            </div>
            <div className="space-y-2">
              <Label>URL Vidéo (optionnel)</Label>
              <Input value={formData.video_url || ''} onChange={(e) => setFormData({...formData, video_url: e.target.value})} />
            </div>
            <div className="space-y-2">
              <Label>URL YouTube (optionnel)</Label>
              <Input value={formData.youtube_url || ''} onChange={(e) => setFormData({...formData, youtube_url: e.target.value})} />
            </div>
            <div className="space-y-2">
              <Label>Tags (une par ligne)</Label>
              <Textarea 
                value={(formData.tags || []).join('\n')} 
                onChange={(e) => setFormData({...formData, tags: e.target.value.split('\n').filter(f => f.trim())})}
                rows={2}
              />
            </div>
            <div className="space-y-2">
              <Label>Résultats (une par ligne)</Label>
              <Textarea 
                value={(formData.results || []).join('\n')} 
                onChange={(e) => setFormData({...formData, results: e.target.value.split('\n').filter(f => f.trim())})}
                rows={2}
              />
            </div>
            <div className="flex items-center gap-2">
              <Switch 
                checked={formData.is_featured || false} 
                onCheckedChange={(checked) => setFormData({...formData, is_featured: checked})}
              />
              <Label>Projet mis en avant</Label>
            </div>
          </div>
        );

      case 'partner':
        return (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Nom du partenaire</Label>
              <Input value={formData.name || ''} onChange={(e) => setFormData({...formData, name: e.target.value})} />
            </div>
            <div className="space-y-2">
              <Label>Logo</Label>
              <div className="flex gap-2">
                <Input value={formData.logo_url || ''} onChange={(e) => setFormData({...formData, logo_url: e.target.value})} placeholder="URL ou upload" />
                <label className="cursor-pointer">
                  <Button variant="outline" as="span" disabled={uploading}>
                    <Upload className="h-4 w-4" />
                  </Button>
                  <input type="file" className="hidden" accept="image/*" onChange={(e) => handleUpload(e, 'logo_url')} />
                </label>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Site web (optionnel)</Label>
              <Input value={formData.website_url || ''} onChange={(e) => setFormData({...formData, website_url: e.target.value})} />
            </div>
          </div>
        );

      case 'testimonial':
        return (
          <div className="space-y-4">
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label>Nom</Label>
                <Input value={formData.name || ''} onChange={(e) => setFormData({...formData, name: e.target.value})} />
              </div>
              <div className="space-y-2">
                <Label>Poste</Label>
                <Input value={formData.role || ''} onChange={(e) => setFormData({...formData, role: e.target.value})} />
              </div>
            </div>
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label>Entreprise</Label>
                <Input value={formData.company || ''} onChange={(e) => setFormData({...formData, company: e.target.value})} />
              </div>
              <div className="space-y-2">
                <Label>Service concerné</Label>
                <Select value={formData.service || ''} onValueChange={(v) => setFormData({...formData, service: v})}>
                  <SelectTrigger><SelectValue placeholder="Sélectionner..." /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="web">Web</SelectItem>
                    <SelectItem value="graphique">Graphique</SelectItem>
                    <SelectItem value="video">Vidéo</SelectItem>
                    <SelectItem value="marketing">Marketing</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Témoignage</Label>
              <Textarea value={formData.quote || ''} onChange={(e) => setFormData({...formData, quote: e.target.value})} rows={3} />
            </div>
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label>Note (1-5)</Label>
                <Select value={String(formData.rating || 5)} onValueChange={(v) => setFormData({...formData, rating: parseInt(v)})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {[1,2,3,4,5].map(n => <SelectItem key={n} value={String(n)}>{n} étoile{n > 1 ? 's' : ''}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Type de projet</Label>
                <Input value={formData.project_type || ''} onChange={(e) => setFormData({...formData, project_type: e.target.value})} placeholder="Site vitrine + SEO" />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Photo/Avatar</Label>
              <div className="flex gap-2">
                <Input value={formData.avatar || ''} onChange={(e) => setFormData({...formData, avatar: e.target.value})} placeholder="URL ou upload" />
                <label className="cursor-pointer">
                  <Button variant="outline" as="span" disabled={uploading}>
                    <Upload className="h-4 w-4" />
                  </Button>
                  <input type="file" className="hidden" accept="image/*" onChange={(e) => handleUpload(e, 'avatar')} />
                </label>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Switch 
                checked={formData.is_video || false} 
                onCheckedChange={(checked) => setFormData({...formData, is_video: checked})}
              />
              <Label>Témoignage vidéo</Label>
            </div>
            {formData.is_video && (
              <div className="space-y-2">
                <Label>URL YouTube</Label>
                <Input value={formData.youtube_url || ''} onChange={(e) => setFormData({...formData, youtube_url: e.target.value})} placeholder="https://youtube.com/watch?v=..." />
              </div>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  const getTitle = () => {
    const titles = {
      'service-family': 'Famille de service',
      'solution': 'Solution/Service',
      'team': 'Membre de l\'équipe',
      'portfolio': 'Projet/Réalisation',
      'partner': 'Partenaire',
      'testimonial': 'Témoignage'
    };
    return (modal.data?.id ? 'Modifier ' : 'Nouveau ') + (titles[modal.type] || '');
  };

  return (
    <Dialog open={modal.open} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{getTitle()}</DialogTitle>
        </DialogHeader>
        {renderForm()}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annuler</Button>
          <Button onClick={() => onSave(formData)} disabled={uploading}>
            <Save className="h-4 w-4 mr-2" />
            Enregistrer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AdminCMSPage;
